import React, { useCallback, useEffect, useState } from 'react';
import t from 'react-translate';
import { css } from '@emotion/react';

// redux
import { useSelector } from 'react-redux';
import { useAppDispatch } from 'redux/store';
import { getCurrentCourse, getFlatCourseAliases } from 'redux/selectors/course';
import { canConvertToCollectionLecture, getCanConvertToCollectionLesson } from 'redux/actions/collections';

// hooks
import { useLecturePageFromParams, useLecturePageParams } from 'lecture_pages/hooks/lecture-routing';
import { useHideAddUIOnOpen } from 'lecture_pages/hooks/lecture-page-edit-top-panel';

// components
import AddToCollectionModal from 'courses/components/course-lesson-to-collection/add-to-collection-modal';
import TopPanelActionButton from './top-panel-action-button';
import { config } from '../../../../../config/config.json';

const AddToCollectionButton = () => {
  const params = useLecturePageParams();
  const dispatch = useAppDispatch();
  const lecturePage = useLecturePageFromParams();
  const currentCourse = useSelector(getCurrentCourse);
  const { canConvertToCollectionLesson, canLessonContentConvertibleToCollection } = useSelector(state => state.app.collection);
  const courseAliases = useSelector(state => getFlatCourseAliases(state, params.catalogId));
  const disableAddToLessonLibrary = !lecturePage?.isLinked && canLessonContentConvertibleToCollection?.[lecturePage?.id];
  const [showAddToLessonModal, setShowAddToLessonModal] = useState(false);
  const hideAddUIOnOpen = useHideAddUIOnOpen();

  useEffect(() => {
    // Don't run if lecturePage hasn't loaded yet
    if (!lecturePage?.id) return;
    // Make these requests only within a course lesson.
    if (!currentCourse?.isContentManagementCollection) {
      /**
       * To display the 'Add to Lesson Library' option, check if the user has a
       * collection with at least one folder available to convert a lesson
       * from a course to the collection.
       */
      if (canConvertToCollectionLesson === null) {
        dispatch(getCanConvertToCollectionLesson());
      }

      // Check that the current lesson contains only components supported by the Lesson Library.
      const canConvertLesson = canLessonContentConvertibleToCollection?.[lecturePage?.id];
      if (
        !lecturePage?.isLinked
        && canConvertToCollectionLesson
        && (canConvertLesson === undefined || canConvertLesson === null)
      ) {
        dispatch(canConvertToCollectionLecture({
          id: lecturePage?.id,
        }));
      }
    }
  }, [
    currentCourse?.isContentManagementCollection, canConvertToCollectionLesson,
    canLessonContentConvertibleToCollection, dispatch, lecturePage?.id,
    lecturePage?.isLinked,
  ]);

  const addToLessonLibrary = useCallback(() => {
    hideAddUIOnOpen();
    setShowAddToLessonModal(true);
  }, []);

  const closeAddToLessonModal = useCallback(() => {
    setShowAddToLessonModal(false);
  }, []);

  const disableAddToLLTooltipText = lecturePage?.isLinked
    ? t.LECTURE_PAGES.ADMIN.EDIT_MENU_TOP_HEADER.ADD_TO_LL_DISABLED_TEXT(courseAliases)
    : t.LECTURE_PAGES.ADMIN.EDIT_MENU_TOP_HEADER.ADD_TO_LL_NOT_SUPPORTED_DISABLED_TEXT(courseAliases);

  // Hide the button if user doesn't have access to any Lesson Library collections
  if (canConvertToCollectionLesson === false) {
    return null;
  }

  return (
    <div>
      <TopPanelActionButton
        icon='add-to-library'
        tooltipText={!disableAddToLessonLibrary
          ? disableAddToLLTooltipText
          : t.TIMELINE.LESSON_OPTIONS.ADD_TO_COLLECTION()}
        disabled={!disableAddToLessonLibrary}
        onClick={addToLessonLibrary}
        dataQa={config.pendo.lectureEdit.editMenuTopPanel.addToLessonLibraryOption}
        ariaLabel={t.TIMELINE.LESSON_OPTIONS.ADD_TO_COLLECTION()}
      />

      {showAddToLessonModal && (
        <AddToCollectionModal
          show={showAddToLessonModal}
          onClose={closeAddToLessonModal}
          lessonId={lecturePage?.id}
        />
      )}
    </div>
  );
};

export default AddToCollectionButton;
